/*
 *
 * SectorsMap
 *
 */

import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Helmet } from "react-helmet"
import Dropzone from "react-dropzone"
import { Button, Dropdown, Menu, Modal, Tag, notification } from "antd"
import { MoreOutlined } from "@ant-design/icons"
import { Icon, Icons } from "components"
import { setBreadcrumb, setLoader } from "store/actions"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { humanFileSize } from "utils/utils"
import SectorsMapView from "./components/SectorsMapView"
import {
	XLSX_MIME,
	downloadBytes,
	summarizeFailures,
	buildSectorsFile,
	loadDraft,
	saveDraft,
	clearDraft,
} from "./utils"
import "./styles.scss"

const SectorsMap: React.FC = () => {
	const dispatch = useDispatch()
	const [file, setFile] = useState<File | null>(null)
	const [fileName, setFileName] = useState("")
	const [orders, setOrders] = useState<any[] | null>(null)
	// Snapshot for "Reverter para Original", taken right after the file loads.
	const originalOrdersRef = useRef<any[] | null>(null)
	const [hasSectorChanges, setHasSectorChanges] = useState(false)
	// Stack of pre-change snapshots, for "Reverter Última Alteração".
	const [history, setHistory] = useState<any[][]>([])
	const [onlyBike, setOnlyBike] = useState<number | null>(null)
	const [generatedFile, setGeneratedFile] = useState<{
		bytes: Uint8Array
		fileName: string
	} | null>(null)
	const [businesses, setBusinesses] = useState<any[]>([])
	const [businessId, setBusinessId] = useState<string | undefined>()
	const [depots, setDepots] = useState<
		{ name: string; lat: number; lng: number }[]
	>([])
	const [availableSectors, setAvailableSectors] = useState<string[]>([])
	const [hiddenSectors, setHiddenSectors] = useState<Set<string>>(new Set())
	const [hiddenSectorsPrompt, setHiddenSectorsPrompt] = useState<{
		resolve: (action: "include" | "exclude" | null) => void
	} | null>(null)

	// Runs before the "default to Delta" effect below, so that one doesn't clobber it.
	useEffect(() => {
		const draft = loadDraft()
		if (!draft) return

		setFileName(draft.fileName)
		setOrders(draft.orders)
		originalOrdersRef.current = draft.originalOrders
		setHasSectorChanges(draft.hasSectorChanges)
		setHistory(draft.history || [])
		setOnlyBike(draft.onlyBike)
		setHiddenSectors(new Set(draft.hiddenSectors))
		if (draft.businessId) setBusinessId(draft.businessId)
	}, [])

	useEffect(() => {
		API.get({ url: Endpoints.uriBusinesses() }).then((response) => {
			if (response.ok) {
				const fetchedBusinesses = response.data.results?.business || []
				setBusinesses(fetchedBusinesses)

				const delta = fetchedBusinesses.find(
					(business: any) => business.name?.toLowerCase() === "delta"
				)
				// Functional update: don't override a business restored from draft.
				if (delta) setBusinessId((prev) => prev ?? delta._id)
			}
		})
	}, [])

	// Keep the draft in sync while a file is loaded, so a refresh restores it.
	useEffect(() => {
		if (!orders) return
		saveDraft({
			fileName,
			orders,
			originalOrders: originalOrdersRef.current || orders,
			onlyBike,
			businessId,
			hiddenSectors: Array.from(hiddenSectors),
			hasSectorChanges,
			history,
		})
	}, [
		orders,
		fileName,
		onlyBike,
		businessId,
		hiddenSectors,
		hasSectorChanges,
		history,
	])

	useEffect(() => {
		dispatch(
			setBreadcrumb(() => ({
				locations: [{ text: Strings.sectorsMap.title, icon: "location" }],
				actions: [
					{
						type: "select",
						text: Strings.businesses.business,
						placeholder: Strings.orders.selectBusiness,
						value: businessId,
						options: businesses.map((business) => ({
							value: business._id,
							text: business.name,
						})),
						onChange: setBusinessId,
						minWidth: 170,
						maxWidth: 170,
						allowClear: true,
					},
				],
			}))
		)

		return () => {
			dispatch(setBreadcrumb(null))
		}
	}, [dispatch, businesses, businessId])

	useEffect(() => {
		if (!businessId) {
			setDepots([])
			setAvailableSectors([])
			return
		}

		API.get({
			url: Endpoints.v2.uriBusinesses({ businessId, includeContracts: true }),
		}).then((response) => {
			if (!response.ok) return

			const contracts = response.data.results?.[0]?.contracts || []
			const uniqueDepots = new Map<
				string,
				{ name: string; lat: number; lng: number }
			>()
			const lots = new Set<string>()

			contracts.forEach((contract: any) => {
				const coordinates = contract.basePoint?.coordinates
				if (Array.isArray(coordinates) && coordinates.length === 2) {
					const name = contract.name
					uniqueDepots.set(name, {
						name,
						lat: coordinates[1],
						lng: coordinates[0],
					})
				}

				;(contract.lots || []).forEach((lot: string) => lots.add(lot))
			})

			setDepots(Array.from(uniqueDepots.values()))
			setAvailableSectors(Array.from(lots))
		})
	}, [businessId])

	const processFile = async (selectedFile: File) => {
		setFile(selectedFile)
		setOrders(null)
		dispatch(setLoader(true))

		const data = new FormData()
		data.append("file", selectedFile)
		data.append("business", businessId || "")

		try {
			const response = await API.post({
				url: Endpoints.v2.uriSectorsMap("process"),
				data,
				axiosConfig: { timeout: 0 },
			})

			if (response.ok) {
				const {
					file: base64File,
					fileName,
					onlyBike: resultOnlyBike,
				} = response.data.results || {}
				const XLSX = await import("xlsx")
				const bytes = Uint8Array.from(atob(base64File), (c) => c.charCodeAt(0))
				const workbook = XLSX.read(bytes, { type: "array" })
				const sheet = workbook.Sheets[workbook.SheetNames[0]]
				// defval: keeps blank cells as "" instead of dropping the column entirely.
				const parsedOrders = XLSX.utils.sheet_to_json(sheet, { defval: "" })
				setOrders(parsedOrders)
				setFileName(selectedFile.name)
				originalOrdersRef.current = parsedOrders
				setHasSectorChanges(false)
				setHistory([])
				setOnlyBike(resultOnlyBike ?? null)
				setGeneratedFile({ bytes, fileName: fileName || selectedFile.name })
			} else {
				notification.error({
					message: Strings.sectorsMap.title,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
				setFile(null)
			}
		} catch (err) {
			notification.error({
				message: Strings.sectorsMap.title,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
			setFile(null)
		}

		dispatch(setLoader(false))
	}

	const updateSectors = (indices: number[], sector: string) => {
		if (orders) setHistory((prev) => [...prev, orders])
		const indexSet = new Set(indices)
		setOrders(
			(prev) =>
				prev && prev.map((o, i) => (indexSet.has(i) ? { ...o, sector } : o))
		)
		setHasSectorChanges(true)
	}

	const revertSectors = () => {
		if (!originalOrdersRef.current) return
		setOrders(originalOrdersRef.current.map((o) => ({ ...o })))
		setHasSectorChanges(false)
		setHistory([])
		notification.success({
			message: Strings.sectorsMap.changesReverted,
			placement: "bottomRight",
			duration: 3,
		})
	}

	const revertLastChange = () => {
		setHistory((prev) => {
			if (prev.length === 0) return prev
			const lastOrders = prev[prev.length - 1]
			setOrders(lastOrders)
			setHasSectorChanges(prev.length > 1)
			return prev.slice(0, -1)
		})
	}

	const toggleSectorVisibility = (sector: string) => {
		setHiddenSectors((prev) => {
			const next = new Set(prev)
			if (next.has(sector)) next.delete(sector)
			else next.add(sector)
			return next
		})
	}

	// Hidden sectors are purely visual — ask whether to include them before
	// generating/creating anything real. Null return means cancelled.
	const resolveOrdersConsideringHidden = async (): Promise<any[] | null> => {
		if (!orders) return null
		if (hiddenSectors.size === 0) return orders

		const action = await new Promise<"include" | "exclude" | null>((resolve) =>
			setHiddenSectorsPrompt({ resolve })
		)
		setHiddenSectorsPrompt(null)

		if (!action) return null
		if (action === "include") return orders
		return orders.filter((o) => !hiddenSectors.has(String(o.sector)))
	}

	const reset = () => {
		setFile(null)
		setFileName("")
		setOrders(null)
		originalOrdersRef.current = null
		setHasSectorChanges(false)
		setHistory([])
		setOnlyBike(null)
		setGeneratedFile(null)
		setHiddenSectors(new Set())
		clearDraft()
	}

	const downloadFile = () => {
		if (!generatedFile) return
		downloadBytes(generatedFile.bytes, generatedFile.fileName, XLSX_MIME)
	}

	const generateFinalFile = async () => {
		const ordersToUse = await resolveOrdersConsideringHidden()
		if (!ordersToUse) return
		dispatch(setLoader(true))

		try {
			const data = new FormData()
			data.append("file", await buildSectorsFile(ordersToUse), "sectors.xlsx")
			data.append("business", businessId || "")

			const response = await API.post({
				url: Endpoints.v2.uriSectorsMap("generate"),
				data,
				axiosConfig: { timeout: 0 },
			})

			if (response.ok) {
				const { file: base64File, fileName } = response.data.results || {}
				const bytes = Uint8Array.from(atob(base64File), (c) => c.charCodeAt(0))
				downloadBytes(bytes, fileName || "sectors.zip", "application/zip")
			} else {
				notification.error({
					message: Strings.sectorsMap.title,
					description: response.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.sectorsMap.title,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	const createOrders = async () => {
		if (!orders || !businessId) return

		const ordersToUse = await resolveOrdersConsideringHidden()
		if (!ordersToUse) return

		Modal.confirm({
			title: Strings.sectorsMap.createOrders,
			content: Strings.sectorsMap.createOrdersConfirm,
			okText: Strings.generic.confirm,
			cancelText: Strings.generic.cancel,
			onOk: async () => {
				dispatch(setLoader(true))

				try {
					const response = await API.post({
						url: Endpoints.v2.uriSectorsMap("import"),
						data: {
							business: businessId,
							orders: ordersToUse.map((o) => ({ ...o, Lot: o.sector })),
						},
						axiosConfig: { timeout: 0 },
					})

					if (response.ok) {
						const { results } = response.data.results || {}
						const hasIssues = (results || []).some(
							(r: any) => r.error || r.failedCount > 0
						)
						Modal.info({
							title: Strings.sectorsMap.createOrders,
							width: 560,
							content: (
								<div className="SectorsMapCreateOrdersResult">
									{(results || []).map((r: any) => (
										<div
											key={r.file}
											className={`SectorsMapCreateOrdersRow${
												r.error
													? " SectorsMapCreateOrdersRowError"
													: r.failedCount > 0
														? " SectorsMapCreateOrdersRowWarning"
														: ""
											}`}
										>
											<div className="SectorsMapCreateOrdersRowHeader">
												<span className="SectorsMapCreateOrdersFile">
													{r.file}
												</span>
												{r.error ? (
													<Tag color="error">Erro</Tag>
												) : r.successCount === 0 && r.failedCount > 0 ? (
													<Tag color="error">
														{Strings.sectorsMap.allFailed} ({r.failedCount})
													</Tag>
												) : (
													<span>
														<Tag color="success">{r.successCount} ok</Tag>
														{r.failedCount > 0 && (
															<Tag color="warning">{r.failedCount} falhas</Tag>
														)}
													</span>
												)}
											</div>
											{r.error && (
												<div className="SectorsMapCreateOrdersError">
													{r.error}
												</div>
											)}
											{r.failedOrders?.length > 0 && (
												<div className="SectorsMapCreateOrdersFailures">
													{summarizeFailures(r.failedOrders).map(
														([label, failures]) => (
															<details
																key={label}
																className="SectorsMapCreateOrdersFailureGroup"
															>
																<summary>
																	<Tag color="warning">
																		{label}: {failures.length}
																	</Tag>
																	<span className="SectorsMapCreateOrdersFailureToggle">
																		{Strings.sectorsMap.showIds}
																	</span>
																</summary>
																<div className="SectorsMapCreateOrdersFailureIds">
																	{failures
																		.map(
																			(f: any) =>
																				f.orderId ||
																				Strings.orders.missingOrderId
																		)
																		.join(", ")}
																</div>
															</details>
														)
													)}
												</div>
											)}
										</div>
									))}
								</div>
							),
						})
						// Keep the batch on screen if anything failed, so it can be reviewed/retried.
						if (!hasIssues) reset()
					} else {
						notification.error({
							message: Strings.sectorsMap.title,
							description:
								response.data?.message || Strings.serverErrors.wentWrong,
							placement: "bottomRight",
							duration: 5,
						})
					}
				} catch (err) {
					notification.error({
						message: Strings.sectorsMap.title,
						description: Strings.serverErrors.wentWrong,
						placement: "bottomRight",
						duration: 5,
					})
				}

				dispatch(setLoader(false))
			},
		})
	}

	return (
		<>
			<Helmet>
				<title>{Strings.sectorsMap.title}</title>
			</Helmet>
			<div className="SectorsMapContainer">
				{orders && (
					<div className="SectorsMapHeader">
						<div className="HeaderMeta">
							<span className="HeaderFileIcon">
								<Icon name="file" />
							</span>
							<span className="HeaderFileName" title={fileName}>
								{fileName}
							</span>
							<div className="HeaderDivider" />
							<div className="HeaderStats">
								<Tag color="blue">
									<Icon name="package" /> {orders.length}{" "}
									{Strings.sectorsMap.ordersLoaded}
								</Tag>
								<Tag color="gold">
									{onlyBike ?? 0} {Strings.sectorsMap.onlyBike}
								</Tag>
							</div>
						</div>
						<div className="HeaderActions">
							<Dropdown
								trigger={["click"]}
								overlayClassName="SectorsMapMoreMenu"
								overlay={
									<Menu>
										<Menu.Item
											key="download"
											icon={<Icon name="download-folder" />}
											onClick={downloadFile}
										>
											{Strings.sectorsMap.download}
										</Menu.Item>
										<Menu.Item
											key="generate"
											icon={<Icon name="generate-folder" />}
											onClick={generateFinalFile}
										>
											{Strings.sectorsMap.generateFinal}
										</Menu.Item>
										<Menu.Item
											key="new"
											icon={<Icon name="upload-folder" />}
											onClick={reset}
										>
											{Strings.sectorsMap.newFile}
										</Menu.Item>
									</Menu>
								}
							>
								<Button
									className="SectorsMapMoreButton"
									icon={<MoreOutlined />}
									title={Strings.sectorsMap.moreOptions}
								/>
							</Dropdown>
							<Button
								icon={<Icon name="back" />}
								disabled={history.length === 0}
								onClick={revertLastChange}
							>
								{Strings.sectorsMap.revertLastChange}
							</Button>
							<Button
								icon={<Icon name="refresh" />}
								disabled={!hasSectorChanges}
								onClick={revertSectors}
							>
								{Strings.sectorsMap.revertChanges}
							</Button>
							<Button
								type="primary"
								disabled={!businessId}
								onClick={createOrders}
							>
								{Strings.sectorsMap.createOrders}
							</Button>
						</div>
					</div>
				)}

				{orders ? (
					<SectorsMapView
						orders={orders}
						depots={depots}
						availableSectors={availableSectors}
						hiddenSectors={hiddenSectors}
						onToggleSectorVisibility={toggleSectorVisibility}
						onUpdateSectors={updateSectors}
					/>
				) : (
					<>
						<Dropzone
							className="SectorsMapDropzone"
							disabled={!businessId}
							multiple={false}
							accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
							onDrop={(files: File[]) => files[0] && processFile(files[0])}
						>
							{file ? (
								<>
									<Icons.UploadFile />
									<p>
										{Strings.sectorsMap.selectedFile}: {file.name}
									</p>
									<p>{humanFileSize(file.size, true)}</p>
								</>
							) : (
								<>
									<Icons.UploadFile />
									<p>
										{businessId
											? Strings.sectorsMap.uploadPrompt
											: Strings.sectorsMap.selectBusinessFirst}
									</p>
								</>
							)}
						</Dropzone>
					</>
				)}
			</div>

			<Modal
				title={Strings.sectorsMap.hiddenSectorsTitle}
				visible={!!hiddenSectorsPrompt}
				onCancel={() => hiddenSectorsPrompt?.resolve(null)}
				footer={[
					<Button
						key="cancel"
						onClick={() => hiddenSectorsPrompt?.resolve(null)}
					>
						{Strings.generic.cancel}
					</Button>,
					<Button
						key="exclude"
						onClick={() => hiddenSectorsPrompt?.resolve("exclude")}
					>
						{Strings.sectorsMap.excludeHiddenSectors}
					</Button>,
					<Button
						key="include"
						type="primary"
						onClick={() => hiddenSectorsPrompt?.resolve("include")}
					>
						{Strings.sectorsMap.includeHiddenSectors}
					</Button>,
				]}
			>
				<p>
					{Strings.sectorsMap.hiddenSectorsMessage}:{" "}
					<strong>{Array.from(hiddenSectors).join(", ")}</strong>
				</p>
			</Modal>
		</>
	)
}

export default SectorsMap
